import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { JOURNEY_REWARD_TYPE, LOCALES } from "../../../constants";
import Decimal from "decimal.js";
import { loadData } from "../../../functions/dataLoader";
import FILTERS from "../../../config/filter_config.json";

const STATUS_BASIC_SCORE = {
    힘: 1,
    체력: 2,
    인내: 3,
    집중: 4,
    보호: 5,
};

const RARITY_BASIC_SCORE = {
    R: 0,
    SR: 1,
    SSR: 2,
};

const calcValueByLevel = (stat, targetLevel, isRate) => {
    const count = Math.floor((targetLevel - stat.unlock_level) / stat.promote_per_level);
    if (count < 0) return 0;
    const base = isRate ? stat.value_rate : stat.value_int;
    const promoteValue = isRate ? stat.value_rate_per_promote : stat.value_int_per_promote;
    return base + count * promoteValue;
};

const getValuesSepLevel = (stat) => {
    const isRate = stat.value_rate !== 0;
    const values = { level_35: 0, level_40: 0, level_45: 0, level_50: 0, isRate };
    values.level_35 = calcValueByLevel(stat, 35, isRate);
    values.level_35_string = isRate ? `${new Decimal(values.level_35).div(100).toNumber().toFixed(2)}%` : values.level_35;
    values.level_40 = calcValueByLevel(stat, 40, isRate);
    values.level_40_string = isRate ? `${new Decimal(values.level_40).div(100).toNumber().toFixed(2)}%` : values.level_40;
    values.level_45 = calcValueByLevel(stat, 45, isRate);
    values.level_45_string = isRate ? `${new Decimal(values.level_45).div(100).toNumber().toFixed(2)}%` : values.level_45;
    values.level_50 = calcValueByLevel(stat, 50, isRate);
    values.level_50_string = isRate ? `${new Decimal(values.level_50).div(100).toNumber().toFixed(2)}%` : values.level_50;
    return values;
};

const getRewardValueString = (reward) => {
    if (!reward.min) return "";
    if (reward.min === reward.max) return reward.min > 0 ? `+${reward.min}` : `${reward.min}`;
    return reward.min > 0 ? `+${reward.min}~${reward.max}` : `${reward.min}~${reward.max}`;
};

const statNames = {
    journeyStartStats: "journey_start_stats",
    trainingEffects: "training_effects",
    telepathyEffects: "telepathy_effects",
    supportQuestEffects: "support_quest_effects",
};

const normalizeArcanas = (originData, potentials, buffs) => {
    const entities = {};
    const favorites = [];
    const ids = [];

    const lang = localStorage.getItem("language") || LOCALES.KO;

    let arcanaFavorites = localStorage.getItem("arcana_favorites");
    if (!arcanaFavorites) {
        arcanaFavorites = [];
        localStorage.setItem("arcana_favorites", JSON.stringify(arcanaFavorites));
    } else {
        favorites.push(...JSON.parse(arcanaFavorites));
    }

    originData.forEach((arcana) => {
        ids.push(arcana.id);

        const parts = [];
        const eventParts = [];
        const parts_ENG = [];
        const eventParts_ENG = [];

        parts.push(
            arcana.name[LOCALES.KO],
            arcana.char_name[LOCALES.KO],
            arcana.main_stat[LOCALES.KO],
            ...arcana.assists.map((e) => e[LOCALES.KO]),
        );
        parts_ENG.push(
            arcana.name[LOCALES.EN],
            arcana.char_name[LOCALES.EN],
            arcana.main_stat[LOCALES.EN],
            ...arcana.assists.map((e) => e[LOCALES.EN]),
        );

        const uniqueEffect = arcana.unique_effect;
        if (uniqueEffect) {
            parts.push(uniqueEffect.name[LOCALES.KO], uniqueEffect.desc[LOCALES.KO]);
            parts_ENG.push(uniqueEffect.name[LOCALES.EN], uniqueEffect.desc[LOCALES.EN]);
        }

        const stats = {
            journeyStartStats: [],
            trainingEffects: [],
            telepathyEffects: [],
            supportQuestEffects: [],
        };

        Object.keys(statNames).forEach((statName) => {
            arcana[statNames[statName]].forEach((stat) => {
                const type = stat.value_type;
                parts.push(type[LOCALES.KO]);
                parts_ENG.push(type[LOCALES.EN]);
                stats[statName].push({ type, ...getValuesSepLevel(stat) });
            });
        });

        const spPotential = potentials.find((e) => e.name[LOCALES.KO] === (arcana.sp_potential ? arcana.sp_potential[LOCALES.KO] : ""));
        if (spPotential) {
            parts.push(arcana.sp_potential[LOCALES.KO]);
            parts_ENG.push(arcana.sp_potential[LOCALES.EN]);
        }

        arcana.events.forEach((event) => {
            eventParts.push(event.name[LOCALES.KO]);
            eventParts_ENG.push(event.name[LOCALES.EN]);

            event.choices.forEach((choice) => {
                [...choice.success_rewards, ...choice.failure_rewards].forEach((rewardRow) => {
                    rewardRow.forEach((reward) => {
                        let obj;
                        if (reward.type === "RT_SE_POTEN") obj = potentials.find((e) => e.id === reward.reward_id);
                        else if (reward.type === "RT_JOURNEY_BUFF") obj = buffs.find((e) => e.id === reward.reward_id);

                        const valueString = getRewardValueString(reward);
                        if (obj) {
                            eventParts.push(JOURNEY_REWARD_TYPE[reward.type][LOCALES.KO], obj.name[LOCALES.KO], obj.desc[LOCALES.KO]);
                            eventParts_ENG.push(JOURNEY_REWARD_TYPE[reward.type][LOCALES.EN], obj.name[LOCALES.EN], obj.desc[LOCALES.EN]);
                            reward.targetObj = obj;
                        } else {
                            eventParts.push(JOURNEY_REWARD_TYPE[reward.type][LOCALES.KO], valueString);
                            eventParts_ENG.push(JOURNEY_REWARD_TYPE[reward.type][LOCALES.EN], valueString);
                        }
                        reward.valueString = valueString;
                    });
                });
            });
        });

        parts.push(...eventParts);
        parts_ENG.push(...eventParts_ENG);

        entities[arcana.id] = {
            id: arcana.id,
            name: arcana.name,
            charName: arcana.char_name,
            rarity: arcana.rarity,
            mainStat: arcana.main_stat,
            assists: arcana.assists,
            spPotential: spPotential ?? null,
            uniqueEffect,
            stats,
            events: arcana.events,
            searchText: parts.join(" "),
            eventSearchText: eventParts.join(" "),
            searchText_ENG: parts_ENG.join(" "),
            eventSearchText_ENG: eventParts_ENG.join(" "),
        };
    });

    ids.sort(
        (a, b) =>
            RARITY_BASIC_SCORE[entities[b].rarity] - RARITY_BASIC_SCORE[entities[a].rarity] ||
            STATUS_BASIC_SCORE[entities[a].mainStat[LOCALES.KO]] - STATUS_BASIC_SCORE[entities[b].mainStat[LOCALES.KO]] ||
            entities[a].name[lang].localeCompare(entities[b].name[lang], lang),
    );

    return { ids, entities, favorites };
};

export const initArcanas = createAsyncThunk("arcanas/init", async () => {
    const [originData, potentials, buffs] = await Promise.all([
        loadData("arcanas.json"),
        loadData("potentials.json"),
        loadData("journey_buffs.json"),
    ]);
    const normalized = normalizeArcanas(originData, potentials, buffs);
    return normalized;
});

const arcanaSlice = createSlice({
    name: "arcanas",
    initialState: {
        ids: [],
        entities: {},
        favorites: [],
        filter: {
            rarity: FILTERS.ARCANA_RARITY[0],
            status: FILTERS.STATUS[0],
        },
        status: "idle",
    },
    reducers: {
        changeFilter: (state, action) => {
            state.filter[action.payload.id] = action.payload.obj;
        },
        toggleFavorite: (state, action) => {
            if (state.favorites.includes(action.payload)) {
                state.favorites = state.favorites.filter((e) => e !== action.payload);
            } else {
                state.favorites.push(action.payload);
            }
            localStorage.setItem("arcana_favorites", JSON.stringify(state.favorites));
        },
    },
    extraReducers: (builder) => {
        builder.addCase(initArcanas.fulfilled, (state, action) => {
            const normalized = action.payload;
            state.ids = normalized.ids;
            state.entities = normalized.entities;
            state.favorites = normalized.favorites;
            // state.filter = {
            //     rarity: FILTERS.ARCANA_RARITY[0],
            //     status: FILTERS.STATUS[0],
            // };
            state.status = "done";
        });
    },
});

export const { changeFilter, toggleFavorite } = arcanaSlice.actions;
export default arcanaSlice.reducer;
